import React from 'react';
import PropTypes from 'prop-types';
import StackItem from 'Components/ComponentLibrary/Atoms/Layout/StackItem';
import VerticalStack from 'Components/ComponentLibrary/Atoms/Layout/VerticalStack';
import Transition from 'Components/ComponentLibrary/Atoms/Transition';
import Container from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Container';
import { pickWithPriority } from 'Helpers/Affordances';
import fr from 'facet-rest';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import RoundedButton from 'Components/ComponentLibrary/Atoms/Inputs/RoundedButton';
import { DS_SIZE_MAP } from 'Components/ComponentLibrary/DesignSystem/sizes';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import HorizontalStack from 'Components/ComponentLibrary/Atoms/Layout/HorizontalStack';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Insight from './Insight';

const useStyles = buildUseStyles('InsightContent', (theme) => ({
  button: {
    padding: 4,
    flex: '1 0 0',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    background: theme.palette.common.white,
  },
  outerContainer: {
    maxWidth: '500px',
  },
}));

const InsightContent = ({
  resource: scenarioInsightResource,
  index,
}) => {
  const { loadModalCollectionByLink } = useModalContext();
  const { loadPageContentLink } = usePageContentContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const classes = useStyles();
  function selectScenario() {
    logStandardizedEvent(categories.clientWhatIf, eventTypes.clickButton, 'Client Scenario Insight - Scenario select button clicked', {
      eventProperties: {
        proposalScenarioId: scenarioInsightResource.attr('proposalScenarioId'),
      },
    });
    const selectLink = scenarioInsightResource
      .relationship(fr.relationships.activationConfirmation.name)
      .link('related')
      .url();

    loadModalCollectionByLink(selectLink);
  }

  function navigateToDetails() {
    logStandardizedEvent(categories.clientWhatIf, eventTypes.clickButton, 'Client Scenario Insight - Scenario details button clicked', {
      eventProperties: {
        proposalScenarioId: scenarioInsightResource.attr('proposalScenarioId'),
      },
    });
    const detailsLink = scenarioInsightResource
      .relationship(fr.relationships.details.name)
      .link('related');

    loadPageContentLink(detailsLink);
  }

  const handleSelect = pickWithPriority(scenarioInsightResource, [
    [fr.relationships.activationConfirmation.name, () => selectScenario()],
  ], () => null);

  const handleViewDetails = pickWithPriority(scenarioInsightResource, [
    [fr.relationships.details.name, () => navigateToDetails()],
  ], () => null);

  return (
    <VerticalStack spacing={3}>
      <StackItem fillsSpace className={classes.insight}>
        <Transition>
          <Container className={classes.outerContainer}>
            <Insight
              resource={scenarioInsightResource}
              onSelect={handleSelect}
            />
          </Container>
        </Transition>
      </StackItem>
      <StackItem position={'bottom'}>
        <HorizontalStack spacing={2} alignment={'centered'}>
          <Container maxWidth="xs" className={classes.container}>
            <RoundedButton
              test="scenario-details"
              onClick={handleViewDetails}
              className={classes.button}
              variant="contained"
              color={'accentBlueMuted2'}
              size={DS_SIZE_MAP.xxs}
            >
              Details
            </RoundedButton>
            <RoundedButton
              test="facet-plan-client-select-scenario-button"
              onClick={handleSelect}
              className={classes.button}
              variant="contained"
              color={'primary'}
              size={DS_SIZE_MAP.xxs}
            >
              {`Select Scenario ${index + 1}`}
            </RoundedButton>
          </Container>
        </HorizontalStack>
      </StackItem>
    </VerticalStack>
  );
};

InsightContent.defaultProps = {
};

InsightContent.propTypes = {
  resource: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

export default InsightContent;
